/**
 * Formatting utilities for analytics components
 */
import { getCellName } from '../../../../utils/cellSlugMap.js';

// API Base URL helper
export function getApiBase() {
  let base = window.BATEMO_API?.baseUrl || '/api/v1';
  // Ensure HTTPS in production
  if (
    window.location.protocol === 'https:' &&
    typeof base === 'string' &&
    base.startsWith('http://')
  ) {
    base = base.replace('http://', 'https://');
  }
  return base;
}

// Format large numbers
export function formatNumber(num) {
  if (!num && num !== 0) return '-';
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toLocaleString('en-US');
}

/**
 * Format a USD amount for the admin spend screens.
 *
 * Lives here rather than in one of the views because BOTH spend screens
 * (AnalyticsAiUsageView + AnalyticsAiSurfaceSpendView) must round dollars
 * identically — two spend screens showing the same amount differently is a
 * support ticket waiting to happen. Accepts a string (the API returns Decimal
 * as string) or a number.
 *
 * Precision widens as the amount shrinks: sub-cent Ion calls are routinely
 * $0.000x, and a fixed 2-decimal format would render them all as "$0.00".
 */
export function formatUSD(str) {
  if (str === null || str === undefined || str === '') return '-';
  const n = typeof str === 'string' ? parseFloat(str) : str;
  if (isNaN(n)) return '-';
  if (n >= 1000) return '$' + n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (n >= 1) return '$' + n.toFixed(2);
  if (n >= 0.01) return '$' + n.toFixed(4);
  return '$' + n.toFixed(6);
}

// Endpoint to human-readable name mapping
export const ENDPOINT_LABELS = {
  '/api/v1/products/cell-count': 'Get cell count',
  '/api/v1/products/list': 'Load product list',
  '/api/v1/products/explorer/v2': 'Cell Explorer (search)',
  '/api/v1/products/': 'Get all products',
  '/api/v1/products': 'Get all products',
  '/api/v1/users/me': 'Load user profile',
  '/api/v1/products/compare': 'Cell comparison',
  '/api/v1/products/benchmarking': 'Benchmarking data',
  '/api/v1/users/login': 'Login',
  '/api/v1/users/register': 'Registration',
  '/api/v1/users/verify-email': 'Verify email',
  '/api/v1/users/refresh': 'Refresh token',
  '/api/v1/users/subscription': 'Check subscription status',
};

/**
 * Convert API endpoint path to human-readable label
 * @param {string} endpoint - Raw API endpoint path
 * @returns {string} Human-readable label
 */
export function formatEndpointLabel(endpoint) {
  if (!endpoint) return 'Unknown';

  // Check for exact match first
  if (ENDPOINT_LABELS[endpoint]) {
    return ENDPOINT_LABELS[endpoint];
  }

  // Check for product ID pattern: /api/v1/products/{hash}
  const productIdMatch = endpoint.match(/\/api\/v1\/products\/([a-f0-9]{16})$/);
  if (productIdMatch) {
    const productId = productIdMatch[1];
    const cellName = getCellName(productId);
    if (cellName && cellName !== productId) {
      return `Cell: ${cellName}`;
    }
    return `Cell: ${productId.substring(0, 8)}...`;
  }

  // Check for product pictures: /api/v1/products/{hash}/pictures/...
  const pictureMatch = endpoint.match(/\/api\/v1\/products\/([a-f0-9]{16})\/pictures/);
  if (pictureMatch) {
    const productId = pictureMatch[1];
    const cellName = getCellName(productId);
    if (cellName && cellName !== productId) {
      return `Image: ${cellName}`;
    }
    return `Load cell image`;
  }

  // Check for startswith patterns
  for (const [pattern, label] of Object.entries(ENDPOINT_LABELS)) {
    if (endpoint.startsWith(pattern)) {
      return label;
    }
  }

  // Fallback: Clean up the path for display
  const parts = endpoint.split('/');
  if (parts.length >= 4) {
    // Return last meaningful segment
    const lastPart = parts[parts.length - 1] || parts[parts.length - 2];
    return lastPart.charAt(0).toUpperCase() + lastPart.slice(1).replace(/-/g, ' ');
  }

  return endpoint.length > 35 ? endpoint.substring(0, 35) + '...' : endpoint;
}
